import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { Subscription, BehaviorSubject, switchMap, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';

import { userPermissionsEnum } from '../../../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';

import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { HeaderAssessmentMessage, HeaderEditorService } from '../../../../../Common/TargetingShared/services/header-editor.service';
import { DistributionErrors, DistributionService, DistributionValidationLevel } from '../../../../../Common/TargetingShared/services/distribution.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { FocusHelper } from '@Common/Forms/helpers/focus.helper';
import { FileUploadModel } from '@Common/Forms/models/file-upload.model';
import { StepsBannerErrorConfigModel } from '../../../../../Common/Misc/models/banner-error-config.model';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';

import { DistributionModel } from '../../../models/distribution.model';
import { DistributionBlockTypeEnum } from '@Discover/Targeting/enums/distribution-block-type.enum';

import { AIDistributionHeaderDialogComponent } from '../AISubjectLineDialog/AI-distribution-header-dialog.component';
import { DistributionStepsEnum } from '../DistributionStepsNavigation/distribution-steps-navigation.component';
import { PersonalizationService } from '@Discover/Targeting/services/personalization.service';
import { ActionKeys } from '@Common/Forms/enums/keys.enum';
import { RecipientType } from '@Discover/Targeting/enums/recipient-type.enum';
import { RecipientEditorService } from '../RecipientEditor/recipient-editor.service';
import { DistributionApi } from '@api/distributions/distribution.api';
import { PersonalizationSummary } from '@api/distributions/model/distribution-personalization.api.model';
import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { DataConnectionService } from '@Core/Users/services/data-connection.service';


@Component({
    selector: 'agpr-distribution-review',
    templateUrl: './distribution-review.component.html',
    styleUrls: ['./distribution-review.component.scss'],
    providers: [
        {
            provide: MediaServiceApi,
            useClass: MediaServiceApi
        },
    ],
    animations: [
        trigger('panelContent', [
            state('hidden', style({
                height: '0',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class DistributionReviewComponent implements OnInit, OnDestroy {

    @Input() compositionErrors: StepsBannerErrorConfigModel[];
    @Input() set attachments(value: FileUploadModel[]) {
        this.attachmentStrings = value.map(attachment => attachment.name);
    };

    @Output() changeStep: EventEmitter<DistributionStepsEnum> = new EventEmitter();

    @ViewChild('panelContentWrapper') public panelContentWrapper: ElementRef;

    distribution: DistributionModel;
    headerAssessmentMessages: HeaderAssessmentMessage;
    overlayContainer: Element;
    openAiPermission: boolean = false;
    attachmentStrings: string[] = [];
    errors: DistributionErrors = new DistributionErrors();
    collapsed: boolean = true;
    animating: boolean = false;

    unlockedSearches: boolean = false;
    includeSavedSearch: boolean = false;
    personalizationSummary: PersonalizationSummary[] = [];
    personalizationLoading: boolean = false;
    personalizationTags: PersonalizationTagEnum[] = [];

    private subscriptions: Subscription[] = [];

    readonly id = 'distributionReview';
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        public distributionService: DistributionService,
        public headerEditorService: HeaderEditorService,
        private currentUserService: CurrentUserStateService,
        public injector: Injector,
        private pageBuilderService: AbstractPageBuilderService,
        public personalizationService: PersonalizationService,
        public recipientEditorService: RecipientEditorService,
        private dataConnectionService: DataConnectionService,
    ) {}

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x),
            ).subscribe(distribution => {
                this.distribution = distribution;
                this.errors = new DistributionErrors();
            }),
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x && !!x.id),
                switchMap(distribution => {
                    if (distribution.sendFrom) {
                        return this.dataConnectionService.emailAccountConnectors$.pipe(
                            map(connectors => {
                                const matchingConnector = connectors.find(connector => connector.id === distribution.sendFrom.connectionId);
                                if (matchingConnector?.grant?.requiresReAuthentication) {
                                    distribution.sendFrom.requiresReAuthentication = true;
                                }
                                return distribution;
                            })
                        );
                    } else {
                        return of(distribution);
                    }
                }),
                switchMap(distribution => {
                    this.distribution = distribution;
                    this.errors = new DistributionErrors();

                    return this.personalizationService.getSummary().pipe(
                        tap(() => this.personalizationLoading = true),
                    );
                })
            ).subscribe((personalizationSummary) => {
                this.personalizationLoading = false;
                this.personalizationSummary = personalizationSummary;
            }),
            this.personalizationService.getPersonalizationTags().subscribe(tags => this.personalizationTags = tags),
            this.headerEditorService.getHeaderAssessmentMessages().subscribe(assessmentMessages => {
                this.headerAssessmentMessages = assessmentMessages;
            }),
            this.distributionService.getValidationErrors$().subscribe(errors => this.errors = errors),
            this.currentUserService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                const searchGroup = recipientInfo.recipientGroups.find(x => x.type === RecipientType.search);
                this.unlockedSearches = searchGroup ? searchGroup.recipients.some(x => !x.isLocked) : false;
            }),
        );
    }

    openDataConnectionsWindow() {
        this.dataConnectionService.openDataConnectionsNewWindow();
    }

    openHeaderEditor() {
        /* If header errors exist when editor is opened, we store the state to
         * determine which ones have been changed once the editor is closed
         */
        const oldErrors = {
            headerHasNoFrom: this.errors.headerHasNoFrom,
            headerHasNoReplyTo: this.errors.headerHasNoReplyTo,
            headerHasInvalidEmail: this.errors.headerHasInvalidEmail,
            headerHasNoSubjectLine: this.errors.headerHasNoSubjectLine
        };

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        this.coreDialogService.open(AIDistributionHeaderDialogComponent, {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                openAiPermission: this.openAiPermission,
                title: 'Discover.targeting.distribution.headerEditor.header',
                aiReferenceId: this.distribution.aiReferenceId
            },
            outputs: {
                dialogClose: () => {
                    const {
                        headerHasNoFrom,
                        headerHasNoReplyTo,
                        headerHasInvalidEmail,
                        headerHasNoSubjectLine
                    } = this.distributionService.getValidationErrors(this.distribution, DistributionValidationLevel.DRAFT);

                    if (oldErrors.headerHasNoFrom) {
                        this.errors.headerHasNoFrom = headerHasNoFrom;
                    }
                    if (oldErrors.headerHasNoReplyTo) {
                        this.errors.headerHasNoReplyTo = headerHasNoReplyTo;
                    }
                    if (oldErrors.headerHasInvalidEmail) {
                        this.errors.headerHasInvalidEmail = headerHasInvalidEmail;
                    }
                    if (oldErrors.headerHasNoSubjectLine) {
                        this.errors.headerHasNoSubjectLine = headerHasNoSubjectLine;
                    }
                    this.distributionService.updateDirtyFlag();
                }
            }
        }, this.injector);
    }

    focusErrorElement(error: StepsBannerErrorConfigModel) {
        this.navigateToComposeStep();
        requestAnimationFrame(() => {
            this.pageBuilderService.notifyBlockDisplayElementChanged();
            if (error.errorId?.includes('foot')) {
                this.pageBuilderService.selectBlock(this.distribution.blocks.find(x => x.type.key === DistributionBlockTypeEnum.footer));
                requestAnimationFrame(() => {
                    FocusHelper.focusError(error.errorId);
                });
            } else {
                FocusHelper.focusError(error.errorId);
            }
        });
    }

    navigateToComposeStep() {
        this.changeStep.emit(DistributionStepsEnum.compose);
    }

    navigateToRecipientStep() {
        this.changeStep.emit(DistributionStepsEnum.editRecipients);
    }

    navigateToPersonalizeStep() {
        this.changeStep.emit(DistributionStepsEnum.personalize);
    }

    toggle(event) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        if (this.animating) {
            return false;
        }

        this.animating = true;
        this.collapsed = !this.collapsed;

        event?.preventDefault();
    }

    learnMore() {
        window.open('https://help.agilitypr.com/en/articles/9362154-personalizations', '_blank');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
